import styles from "./CurrentEmployees.module.css";
import { useEffect, useState } from "react";
import {
  trackFormError,
  trackPageViewUserStep,
  trackSaveAndContinueUserStep,
} from "utils/analytics/trackEvent";
import { Modal } from "@justworkshr/milo-components";
import HealthCensusDrawer from "uicomponents/CensusDrawer/HealthCensusDrawer";
import HealthDependentDrawer from "uicomponents/CensusDrawer/HealthDependentDrawer";
import Prefilled from "uicomponents/CensusTable/Prefilled";
import { CurrentEmployeesType, SingleDependentType } from "types";
import { HealthCensusType } from "uicomponents/CensusTable/types";
import {
  ParticipantTypeEnum,
  useDeleteDependentCensusMutation,
} from "types/generated/operations";
import { errorMessageMap, hasQueryParam } from "utils/helper";
import Alert from "uicomponents/Alert";
import { Button } from "@justworkshr/milo-core";
import { ActionFooter } from "@justworkshr/milo-form";
import CensusNotice from "uicomponents/CensusNotice";

interface IPrefilledHealthCensusProps {
  data: CurrentEmployeesType;
  refetchData: () => void;
  setNextStep: () => void;
  setPreviousStep: () => void;
}

interface IDrawerState {
  status: string;
  employeeData?: HealthCensusType;
  dependentData?: SingleDependentType | null;
  isDependent?: boolean;
}

const PrefilliedHealthCensus = (
  props: IPrefilledHealthCensusProps
): JSX.Element => {
  useEffect(() => {
    window.scrollTo(0, 0);
    trackPageViewUserStep(3, "Current team", 1);
  }, []);

  const [isError, setIsError] = useState(false);
  const [tableError, setTableError] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [isLoadingSteps, setIsLoadingSteps] = useState(false);

  const [deleteDependentCensus, { loading: deleteDependentCensusLoading }] =
    useDeleteDependentCensusMutation();

  const [drawerState, setDrawerState] = useState<IDrawerState>({
    status: "closed",
    employeeData: undefined,
    dependentData: null,
    isDependent: false,
  });

  const { data, setNextStep } = props;

  const [existingCensuses, setExistingCensuses] = useState(
    data?.employeeCensuses || []
  );

  const unsubmittedStatuses = ["IN_PROGRESS", "NOT_STARTED", undefined];
  const wcAppUnsubmitted = unsubmittedStatuses.includes(
    data?.workersCompApplication?.[0]?.status || undefined
  );
  const hiAppUnsubmitted = unsubmittedStatuses.includes(
    data?.applications?.[0]?.status || undefined
  );
  const isCoreApp = hasQueryParam("usecoreapp");
  const showCensusNotice = hiAppUnsubmitted && wcAppUnsubmitted && isCoreApp;

  useEffect(() => {
    if (data?.employeeCensuses) {
      setExistingCensuses(data.employeeCensuses);
    }
  }, [data]);

  const handleNextClick = () => {
    for (const census of existingCensuses) {
      if (!censusComplete(census)) {
        setTableError(true);
        window.scrollTo({ top: 0, behavior: "smooth" });
        return;
      }
      if (
        census.dependentCensuses &&
        census.dependentCensuses.length > 0 &&
        census.dependentCensuses.some((dep) => !censusComplete(dep))
      ) {
        setTableError(true);
        window.scrollTo({ top: 0, behavior: "smooth" });
        return;
      }
    }
    handleSaveAndContinue();
  };

  const handleSaveAndContinue = async () => {
    setTableError(false);
    setIsLoadingSteps(true);
    trackSaveAndContinueUserStep(3, "Current team", 1);
    setNextStep();
  };

  const handleDelete = async (uuid: string, participantType?: string) => {
    try {
      if (!uuid) {
        return true;
      }
      await deleteDependentCensus({
        variables: {
          uuid,
          participantType:
            (participantType
              ?.toUpperCase()
              .replace(/-/g, "_") as ParticipantTypeEnum) || "EMPLOYEE",
        },
      });
      props.refetchData();
      return true;
    } catch (error: any) {
      const errorMessage: string = error?.message;
      setErrorMessage(errorMessageMap[errorMessage] || "Please try again");
      trackFormError(error, errorMessage);
      setIsError(true);
      return false;
    }
  };

  const openCensusDrawer = (employeeData?: HealthCensusType) => {
    setDrawerState({
      status: "animating",
      employeeData: employeeData,
    });
    setTimeout(() => {
      setDrawerState({
        employeeData: employeeData,
        status: "open",
      });
    }, 10);
  };

  const openDependentDrawer = (
    dependentData: SingleDependentType | null,
    employeeData: HealthCensusType,
    isDelete?: boolean
  ) => {
    if (isDelete) {
      handleDelete(dependentData?.uuid || "", dependentData?.participantType);
      return;
    }

    setDrawerState({
      status: "animating",
      employeeData: employeeData,
      dependentData: dependentData,
      isDependent: true,
    });
    setTimeout(() => {
      setDrawerState({
        employeeData: employeeData,
        dependentData: dependentData,
        isDependent: true,
        status: "open",
      });
    }, 10);
  };

  const closeDrawer = (refetch?: boolean) => {
    setTableError(false);
    setDrawerState({
      ...drawerState,
      status: "animating",
    });
    setTimeout(() => {
      setDrawerState({
        status: "closed",
      });
    }, 500);
    if (refetch) props.refetchData();
  };

  const censusComplete = (census: any) =>
    Boolean(
      census.firstName &&
        census.lastName &&
        census.gender &&
        census.dateOfBirth &&
        census.homeZip
    );

  const employeeRows = existingCensuses.map((census) => ({
    censusData: {
      ...census,
      dependents: census.dependentCensuses
        ? census.dependentCensuses.map((dep) => ({
            dependentData: dep,
            complete: censusComplete(dep),
          }))
        : [],
    },
    complete: censusComplete(census),
  }));

  return (
    <>
      <Modal
        isOpen={drawerState.status !== "closed"}
        onClose={() => closeDrawer()}
      >
        {drawerState.status !== "closed" && !drawerState.isDependent && (
          <HealthCensusDrawer
            isOpen={drawerState.status === "open"}
            employeeData={drawerState.employeeData}
            closeDrawer={closeDrawer}
          />
        )}
        {drawerState.status !== "closed" && drawerState.isDependent && (
          <HealthDependentDrawer
            isOpen={drawerState.status === "open"}
            employeeData={drawerState.employeeData}
            dependentData={drawerState.dependentData}
            closeDrawer={closeDrawer}
          />
        )}
      </Modal>
      <div>
        <div className={styles.censusHeading}>
          <h3>Update employee info and add dependents</h3>
          <div className={styles.description}>
            <p>
              To get you an accurate quote, we need additional information for
              all full- and part-time employees (30-hours/week), and their
              dependents.
            </p>
          </div>

          <Prefilled
            censuses={employeeRows}
            onUpdateClick={openCensusDrawer}
            updateDependent={openDependentDrawer}
            flowName="hi"
            hasError={tableError}
          />
          {isError && <Alert message={errorMessage} />}
        </div>

        {showCensusNotice && <CensusNotice className={styles.notice} />}
        <ActionFooter
          actions={[
            <Button
              variant="ghost"
              onClick={props.setPreviousStep}
              key="prefilled-back-button"
            >
              Back
            </Button>,
            <Button
              disabled={isLoadingSteps || deleteDependentCensusLoading}
              onClick={handleNextClick}
              key="prefilled-next-button"
            >
              Save & continue
            </Button>,
          ]}
        />
      </div>
    </>
  );
};

export default PrefilliedHealthCensus;
